import { useState } from 'react'
import Link from 'next/link'
import type {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
  InferGetServerSidePropsType
} from 'next'
import {
  Button,
  IconButton,
  SPACING,
  TextField,
  Typography,
  useTheme
} from '@themenu/design-system'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { getToken } from 'next-auth/jwt'
import { z } from 'zod'

import { signIn } from 'next-auth/react'
import { useRouter } from 'next/router'

import { Layout } from '../components/Layout'

import { logger } from 'lib/analytics/logger'
import { dispatchToaster } from 'lib/utils/notifications'

import { HandledError, isHandledError } from 'utils/error-handler'

type LoginProps = {
  callbackUrl: string | null
}

const LOGIN_SCHEMA = z.object({
  email: z
    .string()
    .min(1, 'Campo de usuário não pode estar vazio')
    .email('Informe um e-mail válido'),
  password: z.string({
    required_error: 'Campo de senha não pode estar vazio'
  })
})

type LoginSchemaType = z.infer<typeof LOGIN_SCHEMA>

const errorMessageByReason: Record<string, string> = {
  locked: 'Seu usuário está bloqueado, entre em contato com o time de suporte'
}

export default function Login({
  callbackUrl
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const { push, query } = useRouter()

  const { colors } = useTheme()

  const [toggleInputType, setToggleInputType] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<LoginSchemaType>({
    mode: 'onChange',
    resolver: zodResolver(LOGIN_SCHEMA),
    defaultValues: {
      email: query['email']?.toString()
    }
  })

  const watchFields = watch()

  const callbackPathname =
    callbackUrl && new URL(decodeURIComponent(callbackUrl)).pathname

  const handleSubmitForm = async () => {
    setIsLoading(true)
    try {
      const response = await signIn('credentials', {
        redirect: false,
        email: watchFields.email,
        password: watchFields.password
      })

      if (!response?.ok) {
        const err = JSON.parse(response?.error as string) as {
          message: string
          reason: string
        }

        throw new HandledError(
          errorMessageByReason[err.reason] ?? err.message,
          err
        )
      }

      push(callbackPathname ?? '/')
    } catch (err) {
      const error = err as Api.LaravelValidationResponse

      if (error.status !== 422) {
        dispatchToaster({
          title: 'Oops!',
          message: isHandledError(error)
            ? error.message
            : 'Ocorreu um erro ao tentar realizar o acesso!',
          variant: 'error'
        })

        logger.error(
          'Erro no login',
          isHandledError(error) ? error.context : {},
          error
        )
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Layout>
      <div className="md:max-w-[360px]">
        <Typography variant="largeTitle">Boas-vindas</Typography>

        <Typography variant="callout" mt={SPACING.nano} mb={SPACING.sm}>
          Faça o login para acessar a plataforma Swile
        </Typography>

        <form onSubmit={handleSubmit(handleSubmitForm)}>
          <TextField
            autoComplete="email"
            id="email"
            type="email"
            label="E-mail"
            error={!!errors.email}
            {...register('email')}
            errorMessage={errors.email ? String(errors?.email?.message) : ''}
            name="email"
          />

          <TextField
            autoComplete="current-password"
            id="password"
            type={toggleInputType ? 'text' : 'password'}
            label="Senha"
            {...register('password')}
            error={!!errors.password}
            errorMessage={
              errors.password ? String(errors?.password?.message) : ''
            }
            name="password"
            mt={24}
            mb={16}
            suffix={
              <IconButton
                key="icon"
                variant="contrast"
                size={32}
                name="eye-open"
                onClick={() => setToggleInputType(!toggleInputType)}
              />
            }
          />

          <Link href="/auth/forgot-password">
            <Typography
              variant="callout"
              color={colors.interactive.primary.default}
              weight="strong"
              mb={SPACING.s}
            >
              <strong>Esqueci minha senha</strong>
            </Typography>
          </Link>

          <div className="flex justify-end">
            <Button
              type="submit"
              component="button"
              variant="primary"
              loading={isLoading}
              size={40}
              endIcon="arrow-right"
            >
              Próximo
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  )
}

export const getServerSideProps = async (
  context: GetServerSidePropsContext
): Promise<GetServerSidePropsResult<LoginProps>> => {
  const { callbackUrl } = context.query

  const session = await getToken({
    req: context.req,
    secret: process.env.NEXTAUTH_SECRET
  })

  if (!session) {
    return {
      props: {
        callbackUrl: (callbackUrl as string) ?? null
      }
    }
  }

  return {
    redirect: {
      destination: '/',
      permanent: false
    }
  }
}

Login.template = 'guest'
